import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { staggerContainer, staggerItem } from './motionVariants';
import { SkeletonRow } from './Skeleton';

/** Rows beyond this index render without entrance animation, so large lists don't cascade for seconds. */
const STAGGER_CAP = 12;

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer for a row. */
  render: (row: T) => ReactNode;
  /** Optional fixed column width. */
  width?: string;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  /** Shown when there are no rows and not loading. */
  emptyTitle?: string;
  emptyHint?: ReactNode;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  emptyTitle = 'Nothing here yet',
  emptyHint,
}: TableProps<T>): JSX.Element {
  const colSpan = columns.length;

  return (
    <div className="w-full overflow-x-auto rounded-[24px] bg-white dark:bg-zinc-900/40">
      <table className="w-full text-left text-[13px] text-zinc-600 dark:text-zinc-300 border-collapse">
        <thead>
          <tr className="border-b border-zinc-100 dark:border-white/5 bg-zinc-50/50 dark:bg-zinc-800/30">
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-6 py-3 font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider text-[11px] whitespace-nowrap"
                style={col.width !== undefined ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        {loading ? (
          <tbody className="divide-y divide-zinc-100 dark:divide-white/5">
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skeleton-${i}`}>
                <td colSpan={colSpan} className="px-4 py-1.5">
                  <SkeletonRow />
                </td>
              </tr>
            ))}
          </tbody>
        ) : rows.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={colSpan} className="px-6 py-16 text-center">
                <div className="flex flex-col items-center justify-center text-zinc-400 dark:text-zinc-500">
                  <span className="text-base font-medium text-zinc-600 dark:text-zinc-300 mb-1">
                    {emptyTitle}
                  </span>
                  {emptyHint !== undefined && <span className="text-sm">{emptyHint}</span>}
                </div>
              </td>
            </tr>
          </tbody>
        ) : (
          <motion.tbody
            initial="hidden"
            animate="show"
            variants={staggerContainer(0.04)}
            className="divide-y divide-zinc-100 dark:divide-white/5"
          >
            <AnimatePresence initial={false}>
              {rows.map((row, index) => {
                const animated = index < STAGGER_CAP;
                return (
                  <motion.tr
                    key={rowKey(row)}
                    {...(animated ? { variants: staggerItem } : { initial: false })}
                    exit={{ opacity: 0 }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={`transition-colors duration-200 ${onRowClick ? 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/5' : ''}`}
                    {...(onRowClick
                      ? {
                          role: 'button',
                          tabIndex: 0,
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          },
                        }
                      : {})}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-6 py-3 whitespace-nowrap">
                        {col.render(row)}
                      </td>
                    ))}
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </motion.tbody>
        )}
      </table>
    </div>
  );
}
